import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FiCpu, 
  FiZap, 
  FiTrendingUp, 
  FiSettings, 
  FiCheckCircle,
  FiArrowRight,
  FiMonitor,
  FiTarget,
  FiShield,
  FiBarChart2,
  FiActivity
} from 'react-icons/fi';
import { trackPageView } from '../utils/analytics';
import './AIOps.css';

const AIOps = () => {
  useEffect(() => {
    // Track page visit with comprehensive analytics
    trackPageView('/ai-ops', {
      page_title: 'LogCrest AI Ops - Next-Gen AI Operations',
      page_category: 'ai-ops'
    });
  }, []);

  const features = [
    {
      icon: <FiCpu />,
      title: 'Autonomous Operations',
      description: 'Self-healing infrastructure that automatically detects and resolves issues without human intervention.'
    },
    {
      icon: <FiZap />,
      title: 'Intelligent Automation',
      description: 'AI-driven automation that learns from your environment and optimizes operations continuously.'
    },
    {
      icon: <FiTrendingUp />,
      title: 'Predictive Analytics',
      description: 'Advanced machine learning models that predict failures and performance issues before they occur.'
    },
    {
      icon: <FiSettings />,
      title: 'Dynamic Optimization',
      description: 'Real-time resource optimization and capacity planning based on AI insights and predictions.'
    }
  ];

  const capabilities = [
    'Autonomous incident detection and resolution',
    'Predictive capacity planning and scaling',
    'Intelligent resource optimization',
    'Automated performance tuning',
    'Self-healing infrastructure management',
    'Dynamic cost optimization',
    'Intelligent workload placement',
    'Automated compliance and governance'
  ];

  const benefits = [
    {
      metric: '80%',
      label: 'Reduction in Manual Tasks',
      description: 'Automated operations management'
    },
    {
      metric: '65%',
      label: 'Faster Issue Resolution',
      description: 'Self-healing capabilities'
    },
    {
      metric: '45%',
      label: 'Cost Optimization',
      description: 'Intelligent resource management'
    },
    {
      metric: '99.9%',
      label: 'System Reliability',
      description: 'Predictive maintenance'
    }
  ];

  return (
    <div className="ai-ops">
      {/* Hero Section */}
      <section className="aiops-hero">
        <div className="container">
          <div className="aiops-hero-content">
            <motion.div
              className="hero-text"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>Next-Gen AI Ops</h1>
              <p>
                Transform your operations with autonomous AI that learns, predicts, and optimizes 
                your infrastructure. Experience self-healing systems, intelligent automation, 
                and predictive analytics that revolutionize how you manage your environment.
              </p>
              <div className="hero-buttons">
                <Link to="/pricing" className="btn btn-secondary">
                  Get Started
                  <FiArrowRight />
                </Link>
              </div>
            </motion.div>
            <motion.div
              className="hero-visual"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="aiops-dashboard">
                <div className="dashboard-header">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span className="dashboard-title">LogCrest AI Ops Center</span>
                </div>
                <div className="dashboard-content">
                  <div className="ai-status">
                    <div className="status-item active">
                      <FiActivity />
                      <span>AI Learning Mode: Active</span>
                    </div>
                    <div className="status-item optimized">
                      <FiTarget />
                      <span>Auto-Optimization: Running</span>
                    </div>
                    <div className="status-item secure">
                      <FiShield />
                      <span>Self-Healing: Enabled</span>
                    </div>
                  </div>
                  <div className="ai-insights">
                    <div className="insight-item">
                      <FiTrendingUp />
                      <span>Predicted: CPU spike in 2h</span>
                    </div>
                    <div className="insight-item">
                      <FiBarChart2 />
                      <span>Optimization: 15% cost saved</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="aiops-features">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>The future of operations is autonomous</h2>
            <div className="grid grid-2">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="feature-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="feature-icon">
                    {feature.icon}
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="aiops-capabilities">
        <div className="container">
          <div className="capabilities-content">
            <motion.div
              className="capabilities-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Revolutionary AI capabilities</h2>
              <p>
                Our next-generation AI Ops platform combines machine learning, automation, 
                and predictive analytics to create truly autonomous operations. Experience 
                infrastructure that thinks, learns, and optimizes itself.
              </p>
              <ul className="capabilities-list">
                {capabilities.map((capability, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiCheckCircle />
                    {capability}
                  </motion.li>
                ))}
              </ul>
              <Link to="/demo" className="btn btn-primary">
                Experience AI Ops
                <FiArrowRight />
              </Link>
            </motion.div>
            <motion.div
              className="capabilities-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="ai-workflow">
                <div className="workflow-step">
                  <div className="step-icon">
                    <FiMonitor />
                  </div>
                  <div className="step-content">
                    <h4>Observe</h4>
                    <p>Continuous monitoring</p>
                  </div>
                </div>
                <div className="workflow-arrow">→</div>
                <div className="workflow-step">
                  <div className="step-icon">
                    <FiCpu />
                  </div>
                  <div className="step-content">
                    <h4>Learn</h4>
                    <p>AI pattern recognition</p>
                  </div>
                </div>
                <div className="workflow-arrow">→</div>
                <div className="workflow-step">
                  <div className="step-icon">
                    <FiZap />
                  </div>
                  <div className="step-content">
                    <h4>Act</h4>
                    <p>Autonomous response</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="aiops-benefits">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Transform your operations</h2>
            <div className="benefits-grid">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="benefit-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="benefit-metric">{benefit.metric}</div>
                  <div className="benefit-label">{benefit.label}</div>
                  <div className="benefit-description">{benefit.description}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="aiops-cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Ready for autonomous operations?</h2>
            <p>
              Join the future of infrastructure management with LogCrest AI Ops. 
              Experience truly autonomous operations that think, learn, and optimize themselves.
            </p>
            <div className="cta-buttons">
              <Link to="/pricing" className="btn btn-primary">
                Contact Sales
                <FiArrowRight />
              </Link>
              <Link to="/demo" className="btn btn-secondary">
                Schedule Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AIOps;
